import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { BiImage, BiX } from 'react-icons/bi';
import { IoIosWarning } from 'react-icons/io';

import Button from '../../../../newComponents/Button';
import Input from '../../../../newComponents/UnformInput';
import FileInput from '../../../../newComponents/FileInput';
import { ModalHandles } from '../../../../newComponents/Modal';

import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import { useSimpleGroup } from '../../../../hooks/simpleGroup';

import GroupsService, { Group } from '../../../../services/GroupsService';

import { IGroup } from '../../../../models';

import APIError from '../../../../errors/APIError';

import getValidationErrors from '../../../../utils/getValidationErrors';

import { Container, Modal, ModalHeader } from './styles';

type SimpleGroupProps = {
  lastGroupIndex: number;
  onSuccessSave(group: Group): void;
};

type FormData = {
  name: string;
};

const SimpleGroup: React.FC<SimpleGroupProps> = ({
  lastGroupIndex,
  onSuccessSave,
}) => {
  const formRef = useRef<FormHandles>(null);
  const modalRef = useRef<ModalHandles>(null);

  const {
    id,
    image,
    name,
    isDrawerOpened,
    handleResetGroup,
    handleChangeGroup,
    handleCloseDrawer,
  } = useSimpleGroup();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = useCallback(() => {
    modalRef.current?.close();

    handleCloseDrawer();

    setTimeout(() => {
      handleResetGroup();
      formRef.current?.reset();
      formRef.current?.setErrors({});
    }, 1000);
  }, [handleCloseDrawer, handleResetGroup]);

  const handleTryCancel = useCallback(() => {
    if (formRef.current) {
      const formData = formRef.current.getData() as FormData;

      if (formData.name || name) {
        if (formData.name !== name || image instanceof File) {
          return modalRef.current?.open();
        }
      }
    }

    return handleCancel();
  }, [name, image, handleCancel]);

  const handleSubmit = useCallback(
    async (data: FormData) => {
      setIsLoading(true);

      formRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          name: Yup.string()
            .max(40, 'No máximo 40 caracteres')
            .required('O nome do grupo é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const formData = new FormData();

        formData.append('name', data.name);
        formData.append('type', '1');

        if (image instanceof File) {
          formData.append('image', image.name);
          formData.append('images', image);
        } else if (typeof image === 'string') {
          formData.append('image', image);
        }

        let group: IGroup;

        if (id) {
          group = await GroupsService.editGroup(user.empresa_id, id, formData);
        } else {
          formData.append('order', String(lastGroupIndex + 1));

          group = await GroupsService.createGroup(user.empresa_id, formData);
        }

        onSuccessSave({
          id: group.id,
          name: group.name,
          order: Number(group.order),
          type: Number(group.type),
          pauseSale: Boolean(group.pauseSale),
        });

        handleCloseDrawer();

        addToast({
          title: `Grupo ${id ? 'editado' : 'criado'} com sucesso!`,
          type: 'success',
        });

        setTimeout(() => {
          handleResetGroup();
          formRef.current?.reset();
        }, 1000);
      } catch (error) {
        let errors = {};

        if (error instanceof Yup.ValidationError) {
          errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        }

        if (error instanceof APIError) {
          addToast({
            title: error.message,
            type: 'error',
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      id,
      image,
      user.empresa_id,
      lastGroupIndex,
      addToast,
      onSuccessSave,
      handleResetGroup,
      handleCloseDrawer,
    ],
  );

  useEffect(() => {
    formRef.current?.setFieldValue('name', name);
  }, [name]);

  return (
    <Container isVisible={isDrawerOpened}>
      <Form ref={formRef} onSubmit={handleSubmit} initialData={{ name }}>
        <header>
          <div className="drawer-title">
            <h1>{id ? 'Editar' : 'Novo'} grupo</h1>
            <button
              type="button"
              onClick={handleTryCancel}
              disabled={isLoading}
            >
              <BiX />
            </button>
          </div>
          <h3>
            {id ? (
              <>
                Você está editando os dados do grupo <strong>{name}</strong>
              </>
            ) : (
              'Alimentação matérias primas, varejo em geral, etc'
            )}
          </h3>
        </header>
        <main>
          <div className="fields">
            <FileInput
              id="simpleGroupImage"
              icon={BiImage}
              title="Escolha uma imagem"
              selectedImage={image}
              acceptableTypes=".jpeg,.jpg,.png"
              subtitle="Formatos: .jpeg, .jpg, .png"
              onRemove={() => handleChangeGroup({ image: undefined })}
              onSelectFile={(selectedFile) =>
                handleChangeGroup({ image: selectedFile })
              }
            />
            <Input
              id="name"
              name="name"
              label="Nome"
              placeholder="Ex.: Promoções"
            />
          </div>
        </main>
        <footer>
          <Button
            type="button"
            variant="outlined"
            disabled={isLoading}
            onClick={handleTryCancel}
          >
            Cancelar
          </Button>
          <Button type="submit" loading={isLoading}>
            Salvar
          </Button>
        </footer>
      </Form>
      <Modal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <div>
            <IoIosWarning />
          </div>
          <h1>Atenção</h1>
          <button type="button" onClick={() => modalRef.current?.close()}>
            <BiX />
          </button>
        </ModalHeader>
        <p>
          Você não salvou suas alterações. Ao realizar a ação todos os dados
          serão perdidos.
          <br />
          <strong>Deseja prosseguir?</strong>
        </p>
        <Button full onClick={handleCancel}>
          Confirmar
        </Button>
        <Button
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </Modal>
    </Container>
  );
};

export default SimpleGroup;
